import { useState, useEffect, useCallback, useRef } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import {
  BookOpen, Heart, Sun, Sparkles, Users, Star, 
  ChevronRight, ChevronLeft, Save, Loader2, Check,
  PenLine, Eye, Lock, Unlock, Quote, Calendar,
  Award, MessageCircle, Clock, Mic, MicOff, Square,
  Image, Video, Download, FileText, Upload, X, Play, Pause
} from "lucide-react";

const CATEGORY_ICONS = {
  daily_joys: Sun,
  life_passions: Heart,
  gratitude_reflections: Sparkles,
  people_who_shaped_me: Users,
  wisdom_for_generations: BookOpen,
  legacy_hopes: Star
};

export default function MemoryJournal() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [categories, setCategories] = useState([]);
  const [prompts, setPrompts] = useState([]);
  const [entries, setEntries] = useState({});
  const [stats, setStats] = useState(null);
  
  // Active state
  const [activeCategory, setActiveCategory] = useState(null);
  const [activePrompt, setActivePrompt] = useState(null);
  const [currentContent, setCurrentContent] = useState("");
  const [isPrivate, setIsPrivate] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  
  // Voice recording state
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [transcribing, setTranscribing] = useState(false);
  const [voiceAvailable, setVoiceAvailable] = useState(false);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const timerRef = useRef(null);
  
  // Media state
  const [uploadingMedia, setUploadingMedia] = useState(false);
  const [attachedMedia, setAttachedMedia] = useState([]);
  const fileInputRef = useRef(null);
  
  // PDF Export
  const [exporting, setExporting] = useState(false);
  
  const loadData = useCallback(async () => {
    try {
      const [categoriesRes, promptsRes, entriesRes, statsRes] = await Promise.all([
        api.get("/phase74/categories"),
        api.get("/phase74/prompts"),
        api.get("/phase74/entries"),
        api.get("/phase74/stats")
      ]);
      
      setCategories(categoriesRes.data.categories || []);
      setPrompts(promptsRes.data.prompts || []);
      setStats(statsRes.data);
      
      // Build entries lookup
      const entriesMap = {};
      (entriesRes.data.entries || []).forEach(entry => {
        entriesMap[entry.prompt_id] = entry;
      });
      setEntries(entriesMap);
      
    } catch (error) {
      console.error("Error loading memory data:", error);
      toast.error("Failed to load memory journal");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
    // Check voice availability
    checkVoiceAvailability();
  }, [loadData]);

  const checkVoiceAvailability = async () => {
    try {
      const res = await api.get("/phase74/voice/status");
      setVoiceAvailable(res.data.available);
    } catch (error) {
      console.warn("Voice status check failed, checking browser capability:", error);
      // Fallback: Check if browser supports getUserMedia
      if (navigator.mediaDevices && navigator.mediaDevices.getUserMedia) {
        setVoiceAvailable(true);
      } else {
        setVoiceAvailable(false);
      }
    }
  };

  const selectPrompt = (prompt) => {
    setActivePrompt(prompt);
    const existing = entries[prompt.id];
    setCurrentContent(existing?.content || "");
    setIsPrivate(existing?.is_private || false);
    setAttachedMedia(existing?.photos || []);
  };

  // Voice Recording Functions
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];
      
      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorder.start(1000); // Collect data every second
      setIsRecording(true);
      setRecordingTime(0);
      
      // Start timer
      timerRef.current = setInterval(() => {
        setRecordingTime(prev => prev + 1);
      }, 1000);
      
      toast.success("Recording started. Speak clearly.");
    } catch (error) {
      console.error("Recording error:", error);
      toast.error("Could not access microphone. Please allow microphone access.");
    }
  };

  const stopRecording = async () => {
    if (!mediaRecorderRef.current) return;
    
    mediaRecorderRef.current.stop();
    mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
    clearInterval(timerRef.current);
    setIsRecording(false);
    
    // Wait for final data
    await new Promise(resolve => setTimeout(resolve, 100));
    
    // Convert to base64 and transcribe
    if (audioChunksRef.current.length > 0) {
      setTranscribing(true);
      try {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        const reader = new FileReader();
        
        reader.onloadend = async () => {
          const base64Audio = reader.result.split(',')[1];
          
          try {
            const res = await api.post("/phase74/voice/transcribe", {
              audio_base64: base64Audio,
              prompt_id: activePrompt.id
            });
            
            // Append transcribed text to content
            setCurrentContent(prev => {
              if (prev.trim()) {
                return prev + "\n\n" + res.data.transcript;
              }
              return res.data.transcript;
            });
            
            toast.success(`Transcribed ${res.data.word_count} words`);
          } catch (error) {
            toast.error("Transcription failed. Please try again or type your response.");
          } finally {
            setTranscribing(false);
          }
        };
        
        reader.readAsDataURL(audioBlob);
      } catch (error) {
        setTranscribing(false);
        toast.error("Failed to process recording");
      }
    }
  };

  const formatRecordingTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Media Upload Functions
  const handleMediaUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    
    setUploadingMedia(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const res = await api.post("/phase74/media/upload", formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      
      setAttachedMedia(prev => [...prev, {
        url: res.data.media_url,
        type: res.data.media_type,
        filename: res.data.filename
      }]);
      
      toast.success(`${res.data.media_type === 'photo' ? 'Photo' : 'Video'} attached`);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to upload media");
    } finally {
      setUploadingMedia(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const removeMedia = (index) => {
    setAttachedMedia(prev => prev.filter((_, i) => i !== index));
  };

  // PDF Export Function
  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const res = await api.get("/phase74/export/pdf");
      
      // Open HTML in new window for printing
      const printWindow = window.open('', '_blank');
      printWindow.document.write(res.data.html);
      printWindow.document.close();
      
      toast.success("Journal opened in new tab. Use Print > Save as PDF");
    } catch (error) {
      toast.error("Failed to export journal");
    } finally {
      setExporting(false);
    }
  };

  const handleSave = async () => {
    if (!activePrompt || !currentContent.trim()) {
      toast.error("Please write something before saving");
      return;
    }
    
    setSaving(true);
    try {
      await api.post("/phase74/entries", {
        prompt_id: activePrompt.id,
        content: currentContent.trim(),
        is_private: isPrivate,
        media_urls: [],
        photos: attachedMedia.filter(m => m.type === 'photo').map(m => m.url),
        videos: attachedMedia.filter(m => m.type === 'video').map(m => m.url),
        entry_type: attachedMedia.length > 0 ? "mixed" : "text"
      });
      
      toast.success("Memory saved");
      
      // Update local state
      setEntries(prev => ({
        ...prev,
        [activePrompt.id]: {
          prompt_id: activePrompt.id,
          content: currentContent.trim(),
          is_private: isPrivate,
          updated_at: new Date().toISOString()
        }
      }));
      
      // Refresh stats
      const statsRes = await api.get("/phase74/stats");
      setStats(statsRes.data);
      
    } catch (error) {
      toast.error("Failed to save memory");
    } finally {
      setSaving(false);
    }
  };

  const getCategoryPrompts = (categoryId) => {
    return prompts.filter(p => p.category === categoryId);
  };

  const getCategoryProgress = (categoryId) => {
    const catPrompts = getCategoryPrompts(categoryId);
    const completed = catPrompts.filter(p => entries[p.id]).length;
    return {
      completed,
      total: catPrompts.length,
      percentage: catPrompts.length > 0 ? Math.round((completed / catPrompts.length) * 100) : 0
    };
  };

  const formatDate = (isoString) => {
    if (!isoString) return "";
    return new Date(isoString).toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-amber-50 to-white" data-testid="memory-loading">
        <div className="text-center space-y-4">
          <Loader2 className="w-12 h-12 animate-spin text-amber-600 mx-auto" />
          <p className="text-slate-600 font-medium">Loading your memory journal...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-amber-50 via-white to-amber-50" data-testid="memory-journal">
      {/* Header */}
      <div className="bg-gradient-to-r from-amber-600 to-orange-500 text-white py-8 px-6">
        <div className="max-w-5xl mx-auto">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-14 h-14 rounded-full bg-white/20 flex items-center justify-center">
              <BookOpen className="w-7 h-7" />
            </div>
            <div>
              <h1 className="text-2xl font-bold">My Life Story</h1>
              <p className="text-amber-100">A Legacy Journal by Goodwin Hospice</p>
            </div>
          </div>
          <p className="text-amber-50 max-w-2xl">
            Preserve your memories, wisdom, and love for future generations. 
            Take your time — there&apos;s no rush. Each story you share is a precious gift.
          </p>
          
          {stats && (
            <div className="mt-6 flex items-center gap-6">
              <div className="flex items-center gap-2">
                <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                  <Check className="w-5 h-5" />
                </div>
                <div>
                  <p className="text-xl font-bold">{stats.completed}/{stats.total_prompts}</p>
                  <p className="text-xs text-amber-100">Stories Shared</p>
                </div>
              </div>
              <div className="flex-1 max-w-xs">
                <Progress value={stats.percentage_complete} className="h-2 bg-white/20" />
                <p className="text-xs text-amber-100 mt-1">{stats.percentage_complete}% Complete</p>
              </div>
              <div className="flex items-center gap-2">
                <Button 
                  variant="secondary" 
                  className="bg-white/20 hover:bg-white/30 text-white border-0"
                  onClick={() => setShowPreview(true)}
                >
                  <Eye className="w-4 h-4 mr-2" />
                  Preview
                </Button>
                <Button 
                  variant="secondary" 
                  className="bg-white/20 hover:bg-white/30 text-white border-0"
                  onClick={handleExportPDF}
                  disabled={exporting}
                >
                  {exporting ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Download className="w-4 h-4 mr-2" />
                  )}
                  Export PDF
                </Button>
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto p-6">
        <Tabs defaultValue="write" className="space-y-6">
          <TabsList>
            <TabsTrigger value="write" className="gap-2">
              <PenLine className="w-4 h-4" />
              Write
            </TabsTrigger>
            <TabsTrigger value="browse" className="gap-2">
              <BookOpen className="w-4 h-4" />
              Browse Topics
            </TabsTrigger>
            <TabsTrigger value="entries" className="gap-2">
              <MessageCircle className="w-4 h-4" />
              My Entries
            </TabsTrigger>
          </TabsList>

          {/* Write Tab */}
          <TabsContent value="write">
            <div className="grid lg:grid-cols-3 gap-6">
              {/* Categories Sidebar */}
              <div className="lg:col-span-1 space-y-3" data-testid="category-sidebar">
                <h3 className="font-semibold text-slate-900 mb-4">Choose a Topic</h3>
                {categories.map((cat) => {
                  const IconComponent = CATEGORY_ICONS[cat.id] || Heart;
                  const progress = getCategoryProgress(cat.id);
                  const isActive = activeCategory === cat.id;
                  
                  return (
                    <button
                      key={cat.id}
                      onClick={() => {
                        setActiveCategory(cat.id);
                        setActivePrompt(null);
                      }}
                      className={`w-full p-4 rounded-xl text-left transition-all ${
                        isActive 
                          ? "bg-amber-100 border-2 border-amber-400" 
                          : "bg-white border border-slate-200 hover:border-amber-300"
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        <div 
                          className="w-10 h-10 rounded-full flex items-center justify-center"
                          style={{ backgroundColor: `${cat.color}20` }}
                        >
                          <IconComponent className="w-5 h-5" style={{ color: cat.color }} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-slate-900">{cat.name}</p>
                          <p className="text-xs text-slate-500 mt-1 line-clamp-1">{cat.description}</p>
                          <div className="flex items-center gap-2 mt-2">
                            <Progress value={progress.percentage} className="h-1 flex-1" />
                            <span className="text-xs text-slate-500">{progress.completed}/{progress.total}</span>
                          </div>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>

              {/* Main Writing Area */}
              <div className="lg:col-span-2">
                {!activeCategory ? (
                  <Card className="h-96 flex items-center justify-center" data-testid="select-prompt">
                    <div className="text-center p-8">
                      <Quote className="w-12 h-12 text-amber-300 mx-auto mb-4" />
                      <h3 className="text-lg font-semibold text-slate-700">Select a Topic to Begin</h3>
                      <p className="text-slate-500 mt-2 max-w-sm">
                        Choose a category from the left to explore prompts and start sharing your memories.
                      </p>
                    </div>
                  </Card>
                ) : !activePrompt ? (
                  <Card data-testid="prompt-list">
                    <CardHeader>
                      <CardTitle>Choose a Prompt</CardTitle>
                      <CardDescription>
                        Select a question to answer. Take your time — these are your stories.
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="space-y-3">
                      {getCategoryPrompts(activeCategory).map((prompt) => {
                        const hasEntry = entries[prompt.id];
                        return (
                          <button
                            key={prompt.id}
                            onClick={() => selectPrompt(prompt)}
                            className={`w-full p-4 rounded-lg text-left transition-all border ${
                              hasEntry
                                ? "bg-green-50 border-green-200 hover:border-green-300"
                                : "bg-white border-slate-200 hover:border-amber-300"
                            }`}
                          >
                            <div className="flex items-start gap-3">
                              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                                hasEntry ? "bg-green-100" : "bg-slate-100"
                              }`}>
                                {hasEntry ? (
                                  <Check className="w-4 h-4 text-green-600" />
                                ) : (
                                  <PenLine className="w-4 h-4 text-slate-400" />
                                )}
                              </div>
                              <div className="flex-1">
                                <p className="font-medium text-slate-900">{prompt.prompt}</p>
                                {prompt.helper_text && (
                                  <p className="text-sm text-slate-500 mt-1">{prompt.helper_text}</p>
                                )}
                              </div>
                              <ChevronRight className="w-5 h-5 text-slate-400" />
                            </div>
                          </button>
                        );
                      })}
                    </CardContent>
                  </Card>
                ) : (
                  <Card data-testid="writing-area">
                    <CardHeader>
                      <Button 
                        variant="ghost" 
                        size="sm" 
                        className="mb-2 -ml-2"
                        onClick={() => setActivePrompt(null)}
                      >
                        <ChevronLeft className="w-4 h-4 mr-1" />
                        Back to prompts
                      </Button>
                      <CardTitle className="text-lg text-amber-800">{activePrompt.prompt}</CardTitle>
                      {activePrompt.helper_text && (
                        <CardDescription>{activePrompt.helper_text}</CardDescription>
                      )}
                    </CardHeader>
                    <CardContent className="space-y-4">
                      {/* Voice Recording Controls */}
                      {voiceAvailable && (
                        <div className="flex items-center gap-3 p-3 bg-amber-50 rounded-lg border border-amber-200">
                          <div className="flex items-center gap-2 flex-1">
                            {isRecording ? (
                              <>
                                <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
                                <span className="text-sm font-medium text-red-600">
                                  Recording... {formatRecordingTime(recordingTime)}
                                </span>
                              </>
                            ) : transcribing ? (
                              <>
                                <Loader2 className="w-4 h-4 animate-spin text-amber-600" />
                                <span className="text-sm text-amber-700">Transcribing your voice...</span>
                              </>
                            ) : (
                              <span className="text-sm text-amber-700">
                                <Mic className="w-4 h-4 inline mr-1" />
                                Tap to record your story with voice
                              </span>
                            )}
                          </div>
                          {isRecording ? (
                            <Button 
                              variant="destructive" 
                              size="sm"
                              onClick={stopRecording}
                              className="gap-1"
                            >
                              <Square className="w-4 h-4" />
                              Stop
                            </Button>
                          ) : (
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={startRecording}
                              disabled={transcribing}
                              className="gap-1 border-amber-300 hover:bg-amber-100"
                            >
                              <Mic className="w-4 h-4" />
                              Record
                            </Button>
                          )}
                        </div>
                      )}
                      
                      <Textarea
                        value={currentContent}
                        onChange={(e) => setCurrentContent(e.target.value)}
                        placeholder="Share your thoughts, memories, and feelings..."
                        className="min-h-[200px] text-base leading-relaxed"
                      />
                      
                      {/* Media Attachments */}
                      <div className="space-y-3">
                        <div className="flex items-center gap-2">
                          <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleMediaUpload}
                            accept="image/*,video/*"
                            className="hidden"
                          />
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => fileInputRef.current?.click()}
                            disabled={uploadingMedia}
                            className="gap-2"
                          >
                            {uploadingMedia ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Upload className="w-4 h-4" />
                            )}
                            Add Photo/Video
                          </Button>
                          <span className="text-xs text-slate-500">
                            Attach memories with photos or videos
                          </span>
                        </div>
                        
                        {/* Attached Media Preview */}
                        {attachedMedia.length > 0 && (
                          <div className="flex flex-wrap gap-2">
                            {attachedMedia.map((media, index) => (
                              <div 
                                key={index}
                                className="relative group bg-slate-100 rounded-lg p-2 flex items-center gap-2"
                              >
                                {media.type === 'photo' ? (
                                  <Image className="w-4 h-4 text-blue-500" />
                                ) : (
                                  <Video className="w-4 h-4 text-purple-500" />
                                )}
                                <span className="text-xs text-slate-600 max-w-[100px] truncate">
                                  {media.filename}
                                </span>
                                <button
                                  onClick={() => removeMedia(index)}
                                  className="text-slate-400 hover:text-red-500"
                                >
                                  <X className="w-4 h-4" />
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between pt-4 border-t">
                        <div className="flex items-center gap-2">
                          <Switch
                            id="private"
                            checked={isPrivate}
                            onCheckedChange={setIsPrivate}
                          />
                          <Label htmlFor="private" className="flex items-center gap-2 cursor-pointer">
                            {isPrivate ? (
                              <>
                                <Lock className="w-4 h-4 text-slate-500" />
                                <span className="text-sm text-slate-600">Private (only you can see)</span>
                              </>
                            ) : (
                              <>
                                <Unlock className="w-4 h-4 text-green-600" />
                                <span className="text-sm text-slate-600">Shared with family</span>
                              </>
                            )}
                          </Label>
                        </div>
                        
                        <div className="flex items-center gap-2">
                          <span className="text-sm text-slate-500">
                            {currentContent.split(/\s+/).filter(w => w).length} words
                          </span>
                          <Button onClick={handleSave} disabled={saving || !currentContent.trim()}>
                            {saving ? (
                              <Loader2 className="w-4 h-4 animate-spin mr-2" />
                            ) : (
                              <Save className="w-4 h-4 mr-2" />
                            )}
                            Save Memory
                          </Button>
                        </div>
                      </div>
                      
                      {entries[activePrompt.id] && (
                        <div className="text-sm text-slate-500 flex items-center gap-2">
                          <Clock className="w-4 h-4" />
                          Last saved: {formatDate(entries[activePrompt.id].updated_at)}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Browse Tab */}
          <TabsContent value="browse">
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="browse-categories">
              {categories.map((cat) => {
                const IconComponent = CATEGORY_ICONS[cat.id] || Heart;
                const progress = getCategoryProgress(cat.id);
                
                return (
                  <Card 
                    key={cat.id} 
                    className="hover:shadow-lg transition-shadow cursor-pointer"
                    onClick={() => {
                      setActiveCategory(cat.id);
                      setActivePrompt(null);
                    }}
                  >
                    <CardContent className="pt-6">
                      <div 
                        className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-4"
                        style={{ backgroundColor: `${cat.color}20` }}
                      >
                        <IconComponent className="w-7 h-7" style={{ color: cat.color }} />
                      </div>
                      <h3 className="font-semibold text-slate-900 text-center">{cat.name}</h3>
                      <p className="text-sm text-slate-500 text-center mt-2">{cat.description}</p>
                      <div className="mt-4">
                        <Progress value={progress.percentage} className="h-2" />
                        <p className="text-xs text-slate-500 text-center mt-2">
                          {progress.completed} of {progress.total} completed
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </TabsContent>

          {/* Entries Tab */}
          <TabsContent value="entries">
            <div className="space-y-6" data-testid="entries-list">
              {Object.keys(entries).length === 0 ? (
                <Card className="py-12">
                  <div className="text-center">
                    <BookOpen className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                    <h3 className="font-semibold text-slate-700">No entries yet</h3>
                    <p className="text-slate-500 mt-2">
                      Start sharing your memories by selecting a topic above.
                    </p>
                  </div>
                </Card>
              ) : (
                categories.map((cat) => {
                  const catEntries = Object.values(entries).filter(e => e.category === cat.id);
                  if (catEntries.length === 0) return null;
                  
                  const IconComponent = CATEGORY_ICONS[cat.id] || Heart;
                  
                  return (
                    <Card key={cat.id}>
                      <CardHeader>
                        <div className="flex items-center gap-3">
                          <div 
                            className="w-10 h-10 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: `${cat.color}20` }}
                          >
                            <IconComponent className="w-5 h-5" style={{ color: cat.color }} />
                          </div>
                          <div>
                            <CardTitle className="text-lg">{cat.name}</CardTitle>
                            <CardDescription>{catEntries.length} memories recorded</CardDescription>
                          </div>
                        </div>
                      </CardHeader>
                      <CardContent className="space-y-4">
                        {catEntries.map((entry) => {
                          const prompt = prompts.find(p => p.id === entry.prompt_id);
                          return (
                            <div 
                              key={entry.prompt_id}
                              className="p-4 bg-slate-50 rounded-lg"
                            >
                              <div className="flex items-start justify-between mb-2">
                                <p className="font-medium text-slate-700">{prompt?.prompt}</p>
                                {entry.is_private && (
                                  <Badge variant="outline" className="flex items-center gap-1">
                                    <Lock className="w-3 h-3" />
                                    Private
                                  </Badge>
                                )}
                              </div>
                              <p className="text-slate-600 whitespace-pre-wrap">{entry.content}</p>
                              <p className="text-xs text-slate-400 mt-3">
                                Recorded {formatDate(entry.updated_at)}
                              </p>
                            </div>
                          );
                        })}
                      </CardContent>
                    </Card>
                  );
                })
              )}
            </div>
          </TabsContent>
        </Tabs>
      </div>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-center text-2xl">My Life Story</DialogTitle>
            <DialogDescription className="text-center">
              A Legacy Journal • Goodwin Hospice
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-8 py-4">
            {categories.map((cat) => {
              const catEntries = Object.values(entries).filter(e => e.category === cat.id && !e.is_private);
              if (catEntries.length === 0) return null;
              
              const IconComponent = CATEGORY_ICONS[cat.id] || Heart;
              
              return (
                <div key={cat.id}>
                  <div className="flex items-center gap-3 mb-4">
                    <IconComponent className="w-6 h-6" style={{ color: cat.color }} />
                    <h3 className="text-lg font-semibold" style={{ color: cat.color }}>{cat.name}</h3>
                  </div>
                  <div className="space-y-4 pl-9">
                    {catEntries.map((entry) => {
                      const prompt = prompts.find(p => p.id === entry.prompt_id);
                      return (
                        <div key={entry.prompt_id}>
                          <p className="text-sm font-medium text-slate-600 italic mb-1">
                            &quot;{prompt?.prompt}&quot;
                          </p>
                          <p className="text-slate-700 whitespace-pre-wrap">{entry.content}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
          
          <DialogFooter className="border-t pt-4">
            <p className="text-xs text-slate-500 flex-1">
              Prepared with love by Goodwin Hospice — Northern Virginia&apos;s Premier Hospice Care
            </p>
            <Button onClick={() => setShowPreview(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
